import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import React, { useContext, useEffect, useRef, useState } from 'react'

import Clevertap from '@/analytics/clevertap/index'
import { getDateTabFares } from "@/api/srp"
import DateSelectorComponent from '@/components/srp/DateSelector/DateSelectorComponent'
import DateTabs from '@/components/srp/DateSelector/DateTabs'
import { getPartnerId } from '@/utils/cfw'
import { isEmpty } from '@/utils/general/browserHelper'
import { dateDiff, dateToEpoch, getFormattedDate, getInitialDateRange, getNewDateRange, getNumberOfDaysBetweenRange } from '@/utils/pages/common/dateHelper'

dayjs.extend(customParseFormat)

type TabDay = { pr: number | string | null; date: string; isCheapest: boolean }

/** Every date in a `DD-MM-YYYY_DD-MM-YYYY` range, with the price still loading (`pr: null`). */
const placeholderDays = (dateRange: string): TabDay[] => {
    const [start, end] = dateRange.split('_').map(date => dayjs(date, 'DD-MM-YYYY'))
    const days: TabDay[] = []
    for (let day = start; !day.isAfter(end, 'day'); day = day.add(1, 'day')) {
        days.push({ pr: null, date: day.format('DD-MM-YYYY'), isCheapest: false })
    }
    return days
}

interface DateSelector {
    screenType?: string
    journeyType?: string
    searchParams: any
    onDateChange: (query: object) => void
    toastHandler: (msg: string) => void
    isPerAdult?: boolean
    showPriceTooltip?: boolean
    sortedFilteredCards: any
    isSrpRevamp?: boolean
}

function DateSelector({
    screenType = 'fullScreen',
    journeyType = 'onward',
    searchParams,
    onDateChange,
    toastHandler,
    isPerAdult = false,
    showPriceTooltip,
    sortedFilteredCards,
    isSrpRevamp = false
}: DateSelector) {
    /** `fullScreen` is the one-way layout; the round trip split screen keeps the legacy ribbon. */
    const isRevampTabs = isSrpRevamp && screenType === 'fullScreen'
    const rangeScreenType = isRevampTabs ? 'srpRevampFullScreen' : screenType
    const journeyTypeOnward = journeyType === 'onward' ? true : false
    const [curDate, updateCurDate] = useState(journeyTypeOnward ? searchParams.depart_date : searchParams.return_date)
    const [dateRange, updateDateRange] = useState('')
    const [tabData, updateTabData] = useState<any>({})
    const [flexiData, setFlexiData] = useState<any>({})
    /** Revamp ribbon: fares per request payload for this mount, so paging back never refetches. */
    const fareCache = useRef(new Map<string, TabDay[]>())
    const inflightFares = useRef(new Map<string, Promise<TabDay[] | null>>())
    /** Only the latest range may paint; a slow earlier response must not overwrite it. */
    const latestRange = useRef('')

    const showTabDays = (days: TabDay[]) => {
        setFlexiData(journeyTypeOnward ? { onward: days } : { onward: days, return: days })
    }

    const fetchTabDays = async (payload: object): Promise<TabDay[] | null> => {
        const curTabData: any = await getDateTabFares(payload)
        if (!(curTabData?.dayData?.length > 0)) return null
        return curTabData.dayData.map((day: any) => ({
            pr: day.farePresent ? Number(day.amount) : '-',
            date: day.date,
            isCheapest: day.fareCategory === 'CHEAPEST'
        }))
    }

    /** Shares one request per payload; a failed or empty response is dropped so the range can retry. */
    const fetchTabDaysCached = (payload: object) => {
        const key = JSON.stringify(payload)
        let request = inflightFares.current.get(key)
        if (!request) {
            request = fetchTabDays(payload)
                .then(days => {
                    if (days) fareCache.current.set(key, days)
                    return days
                })
                .catch(() => null)
                .finally(() => inflightFares.current.delete(key))
            inflightFares.current.set(key, request)
        }
        return request
    }

    const getDateRangePrice = async (dateRange: any) => {
        latestRange.current = dateRange

        const perAdultSearchParams = {...searchParams}
        perAdultSearchParams.adults = 1
        perAdultSearchParams.childs = 0
        perAdultSearchParams.infants = 0

        const searchParam = isPerAdult ? perAdultSearchParams : searchParams
        const depart_date_range = journeyTypeOnward ? dateRange : ''
        const return_date_range = !journeyTypeOnward ? dateRange : ''
        const [startDateOnward] = depart_date_range.split('_')
        const [startDateReturn] = return_date_range.split('_')
        
        const fareCalendarPayload = {
            fareCalendarType: 'CHEAPEST',
            departDetails: journeyTypeOnward ?
                {
                    from: searchParam?.from,
                    to: searchParam?.to,
                    startDate: dateToEpoch(startDateOnward)
                }
                : {

                    from: searchParam?.to,
                    to: searchParam?.from,
                    startDate: dateToEpoch(startDateReturn)
                },
            paxDetails: {
                adult: searchParam?.adults || 1,
                child: searchParam?.childs || 0,
                infant: searchParam?.infants || 0,
            },
            fareDetails: {
                domain: 'IN',
                cabinType: (searchParam.class)?.toUpperCase() === 'PREMIUM ECONOMY' ? 'PREMIUM_ECONOMY' : (searchParam.class)?.toUpperCase() || 'ECONOMY',
                fareType: (searchParam.isCfw === true || searchParam.isCfw === 'true') ? 'CFW' : 'RETAIL',
                fareGroup: (searchParam.sft)?.toUpperCase() || 'REGULAR',
                partnerId: getPartnerId(searchParam.isCfw) || '10000000'
            },
            numberOfDays: journeyTypeOnward ? getNumberOfDaysBetweenRange(depart_date_range) : getNumberOfDaysBetweenRange(return_date_range) ,
            journeyType: journeyTypeOnward ? 'ONE_WAY' : 'ROUND_TRIP',
            departDate: journeyTypeOnward ? '' : dateToEpoch(searchParam.depart_date)
        }

        if (!isRevampTabs) {
            const days = await fetchTabDays(fareCalendarPayload)
            if (days && latestRange.current === dateRange) showTabDays(days)
            return
        }

        // Revamp: move the tabs now and fill prices when they land (instantly when cached).
        const cached = fareCache.current.get(JSON.stringify(fareCalendarPayload))
        showTabDays(cached || placeholderDays(dateRange))
        if (cached) return
        const days = await fetchTabDaysCached(fareCalendarPayload)
        if (latestRange.current !== dateRange) return
        showTabDays(days || placeholderDays(dateRange).map(day => ({ ...day, pr: '-' })))
    }

    useEffect(() => {
        if (!isEmpty(flexiData)) {
            const flexiSearchData = {...flexiData }
            if (!isEmpty(flexiSearchData)) {
                updateTabData(flexiSearchData)
            }
        }
    }, [flexiData])

    useEffect(() => {
        const thisDate = journeyTypeOnward ? searchParams.depart_date : searchParams.return_date
        updateCurDate(thisDate)
        const dateRange = getInitialDateRange({ thisDate, screenType: rangeScreenType })
        updateDateRange(dateRange)
        getDateRangePrice(dateRange)
        console.log('CDM - dateRange ', dateRange)
    }, [searchParams.depart_date, searchParams.return_date, rangeScreenType])

    const updateSelectedDate = (date: any) => {
        const dateToSend: any = {}
        if (journeyTypeOnward) {
            dateToSend.depart_date = getFormattedDate(date, 'DD/MM/YYYY' as any, 'DD-MM-YYYY' as any)
        } else {
            dateToSend.return_date = getFormattedDate(date, 'DD/MM/YYYY' as any, 'DD-MM-YYYY' as any)
        }
        const queryObject = {
            ...searchParams,
            ...dateToSend
        }
        if (dateDiff(queryObject.depart_date, queryObject.return_date, 'DD-MM-YYYY') < 0) {
            toastHandler('Return date cannot be less than onward date.')
            return
        }
        onDateChange(queryObject)
        updateCurDate(date)
        try {
            Clevertap.event('Air_UI_Action', {
                action_type: 'Tab',
                action_name: 'date_tabs'
            })
        } catch (e) {
            console.log(e)
        }
    }

    const handleArrowClick = (direction: any) => {
        if (!searchParams.return_date) {
            const actionName = direction === 'right' ? 'SRP_Calendar_Ribbon_Next_Click' : 'SRP_Calendar_Ribbon_Previous_Click';
            Clevertap.event('Air_UI_Action', {
                action_name: actionName
            })
        }
        const newDateRange = getNewDateRange(dateRange, direction, rangeScreenType)
        updateDateRange(newDateRange)
        getDateRangePrice(newDateRange)
        console.log('handleArrowClick - ', direction)
    }
    if (!tabData.onward && !tabData.return) return ''

    const tabProps = {
        curDate,
        handleArrowClick,
        journeyType,
        sortedFilteredCards,
        tabPriceData: journeyTypeOnward ? tabData.onward : tabData.return,
        onUpdateSelectedDate: updateSelectedDate
    }

    if (isRevampTabs) {
        return <DateTabs {...tabProps} />
    }

    return (
        <>
            <DateSelectorComponent
                {...tabProps}
                showPriceTooltip={showPriceTooltip}
            />
        </>
    )
}

export default DateSelector
